<template>
  <div class="oauth-authorize-page">
    <div class="oauth-authorize-card">
      <h1 class="oauth-title">
        Xác nhận chia sẻ thông tin để đăng nhập hệ thống
        <strong>{{ appName }}</strong>
      </h1>

      <div class="oauth-flow">
        <div class="oauth-avatar-wrap">
          <img v-if="userAvatar" :src="userAvatar" alt="" class="oauth-avatar" />
          <div v-else class="oauth-avatar oauth-avatar--placeholder">{{ userInitial }}</div>
        </div>
        <div class="oauth-flow-line">
          <span class="oauth-shield">🛡</span>
        </div>
        <div class="oauth-app-logo">
          <div class="oauth-app-icon">{{ appInitial }}</div>
        </div>
      </div>
      <p class="oauth-app-name">{{ appName }}</p>

      <p class="oauth-timer">
        Thời gian hết hạn chia sẻ
        <strong class="text-danger">{{ countdown }}</strong>
      </p>

      <p class="oauth-desc">
        Hệ thống định danh Edutalk sẽ chia sẻ, xử lý dữ liệu sau với ứng dụng {{ appName }}.
      </p>

      <button type="button" class="oauth-toggle-info" @click="showInfo = !showInfo">
        <i :class="showInfo ? 'far fa-eye-slash' : 'far fa-eye'"></i>
        {{ showInfo ? 'Ẩn thông tin' : 'Hiện thông tin' }}
      </button>

      <div class="oauth-info-list">
        <p class="oauth-info-title">Các thông tin chia sẻ bắt buộc</p>
        <div class="oauth-info-row">
          <span>Mã nhân sự</span>
          <strong>{{ showInfo ? employeeCode : masked }}</strong>
        </div>
        <div class="oauth-info-row">
          <span>Họ và tên</span>
          <strong>{{ showInfo ? userName : masked }}</strong>
        </div>
        <div class="oauth-info-row">
          <span>Email</span>
          <strong>{{ showInfo ? userEmail : masked }}</strong>
        </div>
        <div class="oauth-info-row">
          <span>Phòng ban</span>
          <strong>{{ showInfo ? userDepartment : masked }}</strong>
        </div>
        <div class="oauth-info-row">
          <span>Loại tài khoản</span>
          <strong>{{ showInfo ? accountType : masked }}</strong>
        </div>
      </div>

      <div class="oauth-purpose">
        <p class="oauth-info-title">Mục đích chia sẻ, xử lý dữ liệu</p>
        <p class="oauth-purpose-text">
          Các thông tin nêu trên được chia sẻ nhằm mục đích thực hiện đăng nhập và xác thực
          quyền truy cập trên hệ thống {{ appName }}.
        </p>
      </div>

      <label class="oauth-consent">
        <input v-model="agreed" type="checkbox" />
        <span>
          Tôi đã đọc và hiểu rõ nội dung mục đích chia sẻ, xử lý dữ liệu nêu trên và đồng ý
          với các nội dung này.
        </span>
      </label>

      <p v-if="error" class="oauth-error">{{ error }}</p>

      <button
        type="button"
        class="btn btn-danger btn-lg btn-block oauth-submit"
        :disabled="!agreed || submitting"
        @click="confirmAuthorize"
      >
        {{ submitting ? 'Đang xử lý...' : 'Đồng ý và đăng nhập' }}
      </button>

      <button type="button" class="btn btn-link btn-block oauth-cancel" @click="cancelAuthorize">
        Hủy
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { OAUTH_AUTHORIZE } from "@/core/services/store/auth.module";

const CLIENT_LABELS = {
  "tochuc-giaovien": "Tổ chức Giáo viên",
};

const CODE_TTL = 300;

export default {
  name: "Authorize",
  data() {
    return {
      showInfo: false,
      agreed: false,
      submitting: false,
      error: "",
      masked: "******",
      expiresAt: Date.now() + CODE_TTL * 1000,
      countdown: "05:00",
      timerId: null,
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    userName() {
      return this.currentUser?.name || "";
    },
    userEmail() {
      return this.currentUser?.email || "";
    },
    userAvatar() {
      return this.currentUser?.image || "";
    },
    userInitial() {
      const name = this.userName || "?";
      return name.charAt(0).toUpperCase();
    },
    employeeCode() {
      const id = this.currentUser?.id;
      return id ? String(id + 100000) : "";
    },
    userDepartment() {
      return this.currentUser?.profile?.branch?.name
        || this.currentUser?.branch
        || "";
    },
    accountType() {
      return this.currentUser?.account_type?.name || "";
    },
    redirectUri() {
      return this.$route.query.redirect_uri || "";
    },
    state() {
      return this.$route.query.state || "";
    },
    clientId() {
      return this.$route.query.client_id || "";
    },
    appName() {
      return CLIENT_LABELS[this.clientId] || this.clientId || "Ứng dụng";
    },
    appInitial() {
      return (this.appName || "A").charAt(0).toUpperCase();
    },
  },
  mounted() {
    if (!this.currentUser || !this.currentUser.id) {
      const returnUrl = this.$route.fullPath;
      return this.$router.replace({ path: "/login", query: { returnUrl } });
    }
    if (!this.redirectUri) {
      this.error = "Thiếu tham số redirect_uri.";
      return;
    }
    if (!this.clientId) {
      this.error = "Thiếu tham số client_id.";
      return;
    }
    this.startCountdown();
  },
  beforeDestroy() {
    if (this.timerId) clearInterval(this.timerId);
  },
  methods: {
    startCountdown() {
      const tick = () => {
        const remain = Math.max(0, this.expiresAt - Date.now());
        const mm = String(Math.floor(remain / 60000)).padStart(2, "0");
        const ss = String(Math.floor((remain % 60000) / 1000)).padStart(2, "0");
        this.countdown = `${mm}:${ss}`;
        if (remain <= 0) {
          clearInterval(this.timerId);
          this.error = "Phiên xác nhận đã hết hạn. Vui lòng thử đăng nhập lại.";
        }
      };
      tick();
      this.timerId = setInterval(tick, 1000);
    },
    async confirmAuthorize() {
      if (!this.agreed || this.submitting) return;
      this.error = "";
      this.submitting = true;
      try {
        const data = await this.$store.dispatch(OAUTH_AUTHORIZE, {
          client_id: this.clientId,
          redirect_uri: this.redirectUri,
          response_type: this.$route.query.response_type || "code",
          state: this.state,
        });
        const code = data?.data?.code;
        if (!code) {
          this.error = data?.message || "Không tạo được mã xác thực.";
          return;
        }
        const url = new URL(this.redirectUri);
        url.searchParams.set("code", code);
        if (this.state) url.searchParams.set("state", this.state);
        window.location.href = url.toString();
      } catch (err) {
        this.error =
          err?.data?.message
          || "Không thể xác nhận chia sẻ thông tin. Vui lòng thử lại.";
      } finally {
        this.submitting = false;
      }
    },
    cancelAuthorize() {
      if (!this.redirectUri) {
        this.$router.replace({ path: "/" });
        return;
      }
      const url = new URL(this.redirectUri);
      url.searchParams.set("error", "access_denied");
      if (this.state) url.searchParams.set("state", this.state);
      window.location.href = url.toString();
    },
  },
};
</script>

<style scoped>
.oauth-authorize-page {
  min-height: 100vh;
  background: #f3f4f6;
  display: flex;
  justify-content: center;
  padding: 24px 16px 40px;
}

.oauth-authorize-card {
  width: 100%;
  max-width: 480px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  padding: 24px 20px 28px;
}

.oauth-title {
  font-size: 1.05rem;
  line-height: 1.5;
  text-align: center;
  margin-bottom: 20px;
  color: #1f2937;
}

.oauth-flow {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 8px;
}

.oauth-avatar-wrap,
.oauth-app-logo {
  width: 72px;
  height: 72px;
  flex-shrink: 0;
}

.oauth-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #e5e7eb;
}

.oauth-avatar--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #8860d0;
  color: #fff;
  font-size: 1.5rem;
  font-weight: 700;
}

.oauth-flow-line {
  flex: 1;
  max-width: 80px;
  height: 2px;
  background: repeating-linear-gradient(90deg, #d4a017 0, #d4a017 6px, transparent 6px, transparent 12px);
  position: relative;
}

.oauth-shield {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  background: #fff;
  padding: 0 4px;
  font-size: 1.1rem;
}

.oauth-app-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(135deg, #0f766e, #134e4a);
  border: 3px solid #d4a017;
}

.oauth-app-icon {
  font-size: 1.75rem;
  font-weight: 700;
  color: #fff;
}

.oauth-app-name {
  text-align: center;
  font-weight: 700;
  color: #374151;
  margin-bottom: 12px;
}

.oauth-timer {
  text-align: center;
  font-size: 0.9rem;
  color: #6b7280;
  margin-bottom: 16px;
}

.oauth-desc {
  font-size: 0.9rem;
  color: #4b5563;
  margin-bottom: 12px;
}

.oauth-toggle-info {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 0.85rem;
  margin-bottom: 14px;
  cursor: pointer;
}

.oauth-info-list,
.oauth-purpose {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 14px;
}

.oauth-info-title {
  font-weight: 700;
  font-size: 0.9rem;
  margin-bottom: 10px;
  color: #111827;
}

.oauth-info-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #eceff3;
  font-size: 0.88rem;
}

.oauth-info-row:last-child {
  border-bottom: none;
}

.oauth-purpose-text {
  font-size: 0.88rem;
  color: #4b5563;
  margin: 0;
  line-height: 1.5;
}

.oauth-consent {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 0.85rem;
  color: #374151;
  margin-bottom: 16px;
  cursor: pointer;
}

.oauth-consent input {
  margin-top: 3px;
}

.oauth-error {
  color: #dc2626;
  font-size: 0.88rem;
  margin-bottom: 12px;
}

.oauth-submit {
  border-radius: 999px;
  font-weight: 700;
  margin-bottom: 8px;
}

.oauth-cancel {
  color: #6b7280;
}
</style>
